import React, { useState, useEffect } from 'react';
import schema from './schema';
import defaultData from './defaultData';
import validator from './validator';
import { calculateHeatPumpFlowRate, evaluateGlycolProtection } from '../../utils/plumbing/g3Calculations';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertTriangle, FiPrinter, FiSave, FiInfo } = FiIcons;

const AirToWaterHeatPumpCommissioningForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const [formData, setFormData] = useState(defaultData);
  const [validation, setValidation] = useState({ isValid: true, errors: {}, warnings: {} });
  const [activeTab, setActiveTab] = useState('installerDetails');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const result = validator.validate(formData);
    setValidation(result);
  }, [formData]);

  const handleChange = (sectionId, fieldId, value) => {
    setFormData(prev => {
      const updatedSection = { ...prev[sectionId], [fieldId]: value };
      
      // Auto-compute flow rate if heat loss or delta T changed
      if (sectionId === 'designParameters' && (fieldId === 'designHeatLossKw' || fieldId === 'designDeltaTC')) {
        const heatLoss = fieldId === 'designHeatLossKw' ? Number(value) : Number(updatedSection.designHeatLossKw || 8);
        const dt = fieldId === 'designDeltaTC' ? Number(value) : Number(updatedSection.designDeltaTC || 5);
        const glycol = Number(prev.waterQualityAndAntifreeze?.glycolConcentrationPct || 25);
        const calc = calculateHeatPumpFlowRate(heatLoss, dt, glycol);
        updatedSection.designFlowRateLpm = calc.flowRateLpm;
      }

      // Auto-update freeze protection temperature if glycol percentage changed
      if (sectionId === 'waterQualityAndAntifreeze' && fieldId === 'glycolConcentrationPct') {
        const evalRes = evaluateGlycolProtection(Number(value), false);
        updatedSection.freezeProtectionTempC = evalRes.freezePointC;
        updatedSection.refractometerReadingBrix = evalRes.refractiveIndexBrix;
      }

      return { ...prev, [sectionId]: updatedSection };
    });
  };

  const handleSave = () => {
    setIsSaving(true);
    if (onSave) {
      onSave(formData);
    }
    setTimeout(() => setIsSaving(false), 600);
  };

  const currentSection = schema.sections.find(s => s.id === activeTab) || schema.sections[0];
  const designCalc = calculateHeatPumpFlowRate(
    formData.designParameters?.designHeatLossKw || 8,
    formData.designParameters?.designDeltaTC || 5,
    formData.waterQualityAndAntifreeze?.glycolConcentrationPct || 25
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-800 text-white p-6 rounded-xl shadow-lg mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-blue-500/30 text-blue-200 text-xs font-semibold px-2.5 py-0.5 rounded border border-blue-400/30">
              MCS MIS 3005
            </span>
            <span className="bg-emerald-500/30 text-emerald-200 text-xs font-semibold px-2.5 py-0.5 rounded border border-emerald-400/30">
              BS EN 14511
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Air-to-Water Heat Pump Commissioning</h1>
          <p className="text-blue-200 text-sm mt-1">MCS MIS 3005 Statutory Handover & Commissioning Checklist</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={() => onGeneratePDF && onGeneratePDF(formData)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </div>

      {/* Real-time Math HUD */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">MCS Design Flow Rate</div>
          <div className="text-2xl font-bold text-blue-400 mt-1">{designCalc.flowRateLpm} <span className="text-sm font-normal text-slate-300">L/min</span></div>
          <div className="text-xs text-slate-400 mt-1">({designCalc.flowRateM3h} m³/h @ ΔT {designCalc.deltaT}°C)</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Measured Flow Rate</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1">
            {formData.commissioningMeasurements?.measuredFlowRateLpm || 0} <span className="text-sm font-normal text-slate-300">L/min</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            ΔT Measured: {formData.commissioningMeasurements?.measuredDeltaTC || 0}°C
          </div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Antifreeze Protection</div>
          <div className="text-2xl font-bold text-amber-400 mt-1">
            {formData.waterQualityAndAntifreeze?.freezeProtectionTempC || -11}°C
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {formData.waterQualityAndAntifreeze?.glycolConcentrationPct || 25}% Glycol ({formData.waterQualityAndAntifreeze?.refractometerReadingBrix || 14}°Bx)
          </div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Validation Status</div>
          <div className="flex items-center gap-2 mt-1">
            {validation.isValid ? (
              <span className="flex items-center gap-1.5 text-emerald-400 font-bold text-lg">
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5" /> Ready for PDF
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-amber-400 font-bold text-lg">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" /> {Object.keys(validation.errors).length} Required
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {Object.keys(validation.warnings).length} Advisory notes
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex overflow-x-auto gap-2 border-b border-slate-200 dark:border-slate-800 pb-3 mb-6 scrollbar-thin">
        {schema.sections.map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveTab(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === section.id
                ? 'bg-blue-600 text-white shadow'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
            }`}
          >
            {section.title}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-800">
          {currentSection.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentSection.fields.map(field => {
            const fieldKey = `${currentSection.id}.${field.id}`;
            const error = validation.errors[fieldKey];
            const warning = validation.warnings[fieldKey];
            const value = formData[currentSection.id]?.[field.id] ?? '';

            return (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>

                {field.type === 'select' ? (
                  <select
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={value}
                    rows={3}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                ) : field.type === 'signature' ? (
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="text"
                      placeholder="Type name or paste digital signature hash"
                      value={value}
                      onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-2"
                    />
                    <span className="text-xs text-slate-400">Electronic verification stamp supported</span>
                  </div>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    step={field.type === 'number' ? 'any' : undefined}
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {error && <p className="text-xs text-red-500 mt-1 font-medium">{error}</p>}
                {warning && <p className="text-xs text-amber-500 mt-1 font-medium">{warning}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AirToWaterHeatPumpCommissioningForm;
